import { type FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Factory as FactoryIcon,
  Mail as MailIcon,
  Lock as LockIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  LogIn as LogInIcon,
  AlertCircle as AlertCircleIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/authStore";
import {
  useSiteConfigStore,
  selectProductName,
  selectLogoUrl,
  selectDarkLogoUrl,
} from "@/stores/siteConfigStore";
import { useTheme } from "@/hooks/useTheme";
import { useNotification } from "@/hooks/useNotification";
import { usePageTitle } from "@/hooks/usePageTitle";
import versionData from "../../version.json";
import styles from "./Login.module.css";

export default function Login() {
  const { t } = useTranslation();
  usePageTitle(t("pageTitle.login"));
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotification();

  const login = useAuthStore((s) => s.login);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);

  const productName = useSiteConfigStore(selectProductName);
  const logoUrl = useSiteConfigStore(selectLogoUrl);
  const darkLogoUrl = useSiteConfigStore(selectDarkLogoUrl);
  const { isDark } = useTheme();

  const currentLogoUrl = isDark && darkLogoUrl ? darkLogoUrl : logoUrl;
  const hasLogo = !!currentLogoUrl;

  const [email, setEmail] = useState(import.meta.env.DEV ? "grv@grv.com.br" : "");
  const [password, setPassword] = useState(import.meta.env.DEV ? "h9e8s3w2" : "");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);

  const isFormValid = email.length > 0 && password.length > 0;

  useEffect(() => {
    const saved = sessionStorage.getItem("login_error_message");
    if (saved) {
      setSessionMessage(saved);
      // Also raise as toast for visibility.
      notify.error(saved, 8000);
      sessionStorage.removeItem("login_error_message");
      const timer = setTimeout(() => setSessionMessage(null), 10_000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [notify]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    clearError();
    setSessionMessage(null);
    const success = await login({ email, password, rememberMe });
    if (success) {
      const redirect = new URLSearchParams(location.search).get("redirect");
      navigate(redirect || "/", { replace: true });
    }
  }

  return (
    <div className={styles.loginForm}>
      <div className={styles.loginHeader}>
        <div className={styles.logo}>
          {hasLogo ? (
            <img
              src={currentLogoUrl!}
              alt={productName}
              className={styles.logoImage}
            />
          ) : (
            <div className={styles.logoIcon}>
              <FactoryIcon size={32} />
            </div>
          )}
          <h1>{productName}</h1>
        </div>
        <p className={styles.loginSubtitle}>
          {t(
            "auth.login.subtitle"
          )}
        </p>
      </div>

      <form onSubmit={onSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.formLabel}>
            {t("auth.login.emailLabel")}
          </label>
          <div className={styles.inputContainer}>
            <MailIcon size={20} className={styles.inputIcon} />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              placeholder={t(
                "auth.login.emailPlaceholder"
              )}
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="password" className={styles.formLabel}>
            {t("auth.login.passwordLabel")}
          </label>
          <div className={styles.inputContainer}>
            <LockIcon size={20} className={styles.inputIcon} />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder={t(
                "auth.login.passwordPlaceholder"
              )}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className={styles.passwordToggle}
              disabled={isLoading}
              aria-label={showPassword ? t("common.hidePassword") : t("common.showPassword")}
            >
              {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
            </button>
          </div>
        </div>

        <div className={styles.formOptions}>
          <label className={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
            />
            {t("auth.login.rememberMe")}
          </label>

          <Link to="/auth/forgot-password" className={styles.forgotLink}>
            {t("auth.login.forgotPassword")}
          </Link>
        </div>

        <button
          type="submit"
          className={styles.loginBtn}
          disabled={isLoading || !isFormValid}
        >
          {isLoading ? (
            <div className={styles.spinner} />
          ) : (
            <LogInIcon size={20} />
          )}
          {isLoading
            ? t("auth.login.signingIn")
            : t("auth.login.signIn")}
        </button>

        <div className={styles.versionDisplay}>
          {t("common.version")}: {versionData.version}
        </div>

        {error ? (
          <div className={styles.errorMessage}>
            <AlertCircleIcon size={16} />
            {error}
          </div>
        ) : null}

        {sessionMessage ? (
          <div className={styles.sessionMessage}>
            <AlertCircleIcon size={16} />
            {sessionMessage}
          </div>
        ) : null}
      </form>
    </div>
  );
}
